import { useState, useMemo, useRef, useCallback, useEffect, RefObject } from 'react'
import EChartsReactCore from 'echarts-for-react/lib/core'
import {
  Box,
  Stack,
  IconButton,
  Popover,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  ToggleButton,
  Tooltip,
} from '@mui/material'

import { MenuIcon, TrushIcon, CopyIcon } from 'shared/icons'
import {
  MoreVert as VertMenuIcon,
  TableView as TableViewIcon,
  CameraAlt as GraphImageViewIcon,
  Fullscreen as MaximizeIcon,
  FullscreenExit as RestoreMaximizeIcon
} from '@mui/icons-material'

import {
  DashBoardCondition,
  DashBoardConditions,
  DashBoardItemData,
  GraphActions,
  GraphOptions,
  GraphType,
} from '../../types'

import { EditDashBoardItem } from './editForm'

import { DownLoadData } from 'components/download/csv/types'
import { DashBoardItemTitle, useDashBoardItemTitles } from './components/title'
import { CsvTableDialog } from 'components/download/csv/dialog'
import { GraphImageDialog } from 'components/download/image/dialog'
import { DashBoardValue } from 'features/dashBoard/hooks'

import { SearchDetailResult } from 'interface/search'
import { TargetListResult } from 'interface/search/targetList'

type DashBoardItemOptionButtonProps = {
  option: GraphOptions
  itemData: DashBoardItemData,
  onChangeItemData: (changed: DashBoardItemData) => void
}
const DashBoardItemOptionButton = ({
  option,
  itemData,
  onChangeItemData
}: DashBoardItemOptionButtonProps) => {
  const state = useMemo(
    () => itemData.options?.find((opt) => opt.id === option.id)?.value === 'true',
    [itemData, option]
  )
  const onClick = useCallback(
    () => {
      const options = itemData.options ?? []
      onChangeItemData({
        ...itemData,
        options: itemData.options?.some((opt) => opt.id === option.id) ? (
          options.map((opt) => (opt.id === option.id) ? { ...opt, value: state ? 'false' : 'true' } : opt)
        ) : (
          options.concat([{ id: option.id, value: state ? 'false' : 'true' }])
        )
      })
    },
    [itemData, option, state, onChangeItemData]
  )
  return (
    <Tooltip title={option.label}>
      <ToggleButton
        value="true"
        size="small"
        selected={state}
        onChange={onClick}
        style={{
          border: 0
        }}
      >
        {option.toolBtn}
      </ToggleButton>
    </Tooltip>
  )
}

type DashBoardItemActionButtonProps = {
  action: GraphActions
  refInstance: RefObject<EChartsReactCore | null>
}
const DashBoardItemActionButton = ({
  action,
  refInstance
}: DashBoardItemActionButtonProps) => {
  const onClick = useCallback(
    () => {
      if (!refInstance.current) return
      action.action(refInstance.current)
    },
    [action, refInstance]
  )
  return (
    <Tooltip title={action.label}>
      <IconButton size="small" onClick={onClick}>
        {action.toolBtn}
      </IconButton>
    </Tooltip>
  )
}

type DashBoardItemProps<T> = {
  dashBoard: DashBoardValue<T>
  search?: SearchDetailResult
  targetList?: TargetListResult
  dataList: T[] | null
  conditions: DashBoardConditions
  onChangeCondition: (
    axisKey?: string,
    changed?: DashBoardCondition | undefined,
    toggle?: boolean
  ) => void
  itemData: DashBoardItemData
  isResizing: boolean
  onChangeItemData: (changed: DashBoardItemData) => void
  onDeleteItem: (id: number) => void
  onCopyItem: (id: number) => void
  onSelected: (id: string) => void
  isMaximize?: boolean
  onMaximize: (id: number) => void
}
export const DashBoardItem = <T,>({
  dashBoard,
  search,
  targetList,
  dataList,
  conditions,
  onChangeCondition,
  itemData,
  isResizing,
  onChangeItemData,
  onDeleteItem,
  onCopyItem,
  onSelected,
  isMaximize,
  onMaximize
}: DashBoardItemProps<T>) => {
  const {
    configure,
    globalAxisKey
  } = dashBoard

  const [graphHeight, setGraphHeight] = useState(0)

  const [popover, setPopover] = useState<{
    dialog: 'editItem' | 'menu'
    element: HTMLButtonElement
    origin: 'left' | 'right'
  } | null>(null)

  const [modal, setModal] = useState<{
    dialog: 'csv' | 'graphImage'
    title: string
    csvData?: DownLoadData
    instance?: EChartsReactCore
  } | null>(null)

  const [csv, setCsv] = useState<{
    builder: () => DownLoadData | undefined
  } | undefined>(undefined)

  const parentBoxRef = useRef<HTMLDivElement | null>(null)
  const graphRef = useRef<EChartsReactCore>(null)

  const graph = useMemo<GraphType | undefined>(
    () => itemData.graphType ? configure.graphs[itemData.graphType] : undefined,
    [itemData.graphType, configure.graphs]
  )
  const titles = useDashBoardItemTitles({
    configure, itemData, globalAxisKey, targetList,
    options: graph?.options
  })
  const hasSubSource = useMemo(
    () => {
      const options = graph?.options?.flatMap((line) => line)
      if (!options) return false
      return itemData.options?.some(({ id, value }) => {
        const option = options.find((def) => def.id === id)
        if (!option) return false
        if (option.type === 'axis' || option.type === 'rank' || option.type === 'time') {
          const key = (value === "@global") ? globalAxisKey : value
          if (!key) return false
          const def = configure.axis[key]
          if (!def) return false
          return def.source !== undefined
        } else if (option.type === 'value') {
          const def = configure.value[value]
          if (!def) return false
          return def.source !== undefined
        }
        return false
      })
    },
    [graph, itemData, globalAxisKey, configure]
  )

  const onClickLeftMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation()
      event.preventDefault()
      setPopover({
        dialog: 'editItem',
        element: event.currentTarget,
        origin: 'left'
      })
    },
    [setPopover]
  )
  const onClickRightMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation()
      event.preventDefault()
      setPopover({
        dialog: 'menu',
        element: event.currentTarget,
        origin: 'right'
      })
    },
    [setPopover]
  )
  const onShowCsvData = useCallback(
    () => {
      const csvData = csv?.builder()
      if (!csvData) return
      setModal({
        dialog: 'csv',
        title: titles.map((word) => `${word.def?.prefix ?? ''}${word.text}${word.def?.suffix ?? ''}`).join(''),
        csvData
      })
    },
    [csv, titles, setModal]
  )
  const onShowGraphImage = useCallback(
    () => {
      const instance = graphRef.current
      if (!instance) return
      setModal({
        dialog: 'graphImage',
        title: titles.map((word) => `${word.def?.prefix ?? ''}${word.text}${word.def?.suffix ?? ''}`).join(''),
        instance
      })
    },
    [graphRef, titles, setModal]
  )

  useEffect(() => {
    const ref = parentBoxRef.current
    if (!ref) return

    const resizeObserver = new ResizeObserver(() => {
      setGraphHeight(ref.clientHeight)
    })
    if (parentBoxRef.current) {
      resizeObserver.observe(parentBoxRef.current)
    }
    return () => {
      resizeObserver.unobserve(ref)
      resizeObserver.disconnect()
    }
  }, [parentBoxRef])
  
  return (
    <>
      <Stack height="100%">
        <Stack
          direction="column"
          sx={{
            boxSizing: 'border-box',
            width: '100%'
          }}
          paddingX={1}
          spacing={1}
          position="relative"
        >
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ userSelect: 'none' }}>
            <IconButton size="small" onClick={onClickLeftMenu}>
              <MenuIcon fontSize="small" />
            </IconButton>

            <DashBoardItemTitle
              id={itemData.id}
              flexGrow={1}
              className="draggable"
              style={{ cursor: 'pointer' }}
              titles={titles}
            />

            {graph?.options?.flatMap(
              (line) => line.filter((option) => option.toolBtn !== undefined)
            ).map((option, i) => (
              (option.fromSubSource === true && !hasSubSource) ? <></> : (
                <DashBoardItemOptionButton
                  key={`dashboard-${itemData.id}-toolbar-option-${i}`}
                  option={option}
                  itemData={itemData}
                  onChangeItemData={onChangeItemData}
                />
              )
            ))}
            {graph?.actions?.map(
              (action, i) => (
                <DashBoardItemActionButton
                  key={`dashboard-${itemData.id}-toolbar-action-${i}`}
                  refInstance={graphRef}
                  action={action}
                />
              )
            )}

            {isMaximize && (
              <Tooltip title="最大化を戻す">
                <IconButton size="small" onClick={() => onMaximize(itemData.id)}>
                  <RestoreMaximizeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={onClickRightMenu}>
              <VertMenuIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        <Box ref={parentBoxRef} flexGrow={1} overflow="hidden">
          {graph && <graph.element<T>
            dashBoard={dashBoard}
            search={search}
            targetList={targetList}
            dataList={dataList}
            conditions={conditions}
            onChangeCondition={onChangeCondition}
            itemData={itemData}
            isResizing={isResizing}
            onSelected={onSelected}
            graphHeight={graphHeight}
            graphRef={graphRef}
            setCsv={setCsv}
          />}
        </Box>
      </Stack>

      {popover && (
        popover.dialog === 'menu' ? (
          <Menu
            id="dashboarditem-menu"
            anchorEl={popover.element}
            open
            keepMounted
            onClose={() => setPopover(null)}
            MenuListProps={{ 'aria-labelledby': 'basic-button' }}
            anchorOrigin={{ vertical: 'bottom', horizontal: popover.origin }}
            transformOrigin={{ vertical: 'top', horizontal: popover.origin }}
          >
            {isMaximize ? (
              <MenuItem onClick={() => {
                onMaximize(itemData.id)
                setPopover(null)
              }}>
                <ListItemIcon>
                  <RestoreMaximizeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>最大化を戻す</ListItemText>
              </MenuItem>
            ) : (
              <MenuItem onClick={() => {
                onMaximize(itemData.id)
                setPopover(null)
              }}>
                <ListItemIcon>
                  <MaximizeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>グラフを最大化</ListItemText>
              </MenuItem>
            )}

            <MenuItem onClick={() => {
              onShowCsvData()
              setPopover(null)
            }}>
              <ListItemIcon>
                <TableViewIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>表データ..</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => {
              onShowGraphImage()
              setPopover(null)
            }}>
              <ListItemIcon>
                <GraphImageViewIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>グラフ画像..</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => {
              onCopyItem(itemData.id)
              setPopover(null)
            }}>
              <ListItemIcon>
                <CopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>複製</ListItemText>
            </MenuItem>

            <Divider/>

            <MenuItem onClick={() => {
              onDeleteItem(itemData.id)
              setPopover(null)
            }}>
              <ListItemIcon>
                <TrushIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>削除</ListItemText>
            </MenuItem>
          </Menu>
        ) : (
          <Popover
            id="item-popover"
            open
            anchorEl={popover.element}
            anchorOrigin={{ vertical: 'bottom', horizontal: popover.origin }}
            transformOrigin={{ vertical: 'top', horizontal: popover.origin }}
            onClose={() => setPopover(null)}
          >
            {popover.dialog === 'editItem' ? (
              <EditDashBoardItem<T>
                itemData={itemData}
                targetList={targetList}
                configure={configure}
                onSubmitForm={(itemData) => {
                  onChangeItemData(itemData)
                  setPopover(null)
                }}
                onClose={() => setPopover(null)}
              />
            ) : (
              <></>
            )}
          </Popover>
        )
      )}
      {modal && (
        (modal.dialog === 'csv' && modal.csvData) ? (
          <CsvTableDialog
            title={modal.title}
            data={modal.csvData}
            onClose={() => setModal(null)}
          />
        ) : (modal.dialog === 'graphImage' && modal.instance) ? (
          <GraphImageDialog
            title={modal.title}
            echartsInstance={modal.instance}
            onClose={() => setModal(null)}
          />
        ) : <></>
      )}
    </>
  )
}
